import { IconPlus } from "@tabler/icons-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPrice } from "@/lib/format-price";
import type { HomeData } from "../use-home-data";

type RecentProductsTableProps = {
  products: HomeData["recentProducts"];
  currency: string;
};

const MS_PER_DAY = 86_400_000;

function formatRelative(
  dateISO: string,
  language: string,
  t: (k: string, opts?: Record<string, unknown>) => string,
) {
  const diffDays = Math.round((Date.now() - Date.parse(dateISO)) / MS_PER_DAY);
  if (diffDays <= 0) {
    return t("dashboard.recent.today");
  }
  if (diffDays === 1) {
    return t("dashboard.recent.yesterday");
  }
  if (diffDays < 30) {
    return t("dashboard.recent.daysAgo", { count: diffDays });
  }
  return new Intl.DateTimeFormat(language, { dateStyle: "medium" }).format(new Date(dateISO));
}

export function RecentProductsTable({ products, currency }: RecentProductsTableProps) {
  const { t, i18n } = useTranslation();

  const rows = useMemo(
    () =>
      products.map((p) => ({
        id: p.id,
        name: p.name,
        price: formatPrice(p.price, currency),
        added: formatRelative(p.createdAt, i18n.language, t),
      })),
    [products, currency, i18n.language, t],
  );

  return (
    <Card className="@container/card flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle>{t("dashboard.recent.title")}</CardTitle>
            <CardDescription>{t("dashboard.recent.description")}</CardDescription>
          </div>
          <Button asChild size="sm" variant="outline">
            <Link to="/menu/products/new">
              <IconPlus className="size-4" />
              {t("dashboard.recent.addProduct")}
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="px-6 pb-6 text-muted-foreground text-sm">
            {t("dashboard.recent.empty")}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("dashboard.recent.columnName")}</TableHead>
                <TableHead className="text-right">{t("dashboard.recent.columnPrice")}</TableHead>
                <TableHead className="text-right">{t("dashboard.recent.columnAdded")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Link
                      className="font-medium hover:underline"
                      to={`/menu/products/${row.id}/edit`}
                    >
                      {row.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{row.price}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{row.added}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
